import { EventTrigger, EventType, UpdaterAppliedTrigger } from '@ekkogmbh/apisdk';
import { useCallback, useMemo, useState } from 'react';
import { createContextHardened } from 'src/Common/Helper/ContextHelper';

/** includes short-cut types that are only defined in the UI */
type ExtendedEventType = EventType | 'dashboard' | 'event-rule-action';

const pruneUnsetProps = (trigger: EventTrigger): EventTrigger => {
  // remove key from internal context keywords
  if (trigger.type === EventType.GENERIC && ['dashboard'].indexOf(trigger.properties.context as string) >= 0) {
    delete trigger.properties.key;
  }

  // remove empty strings
  Object.keys(trigger.properties).forEach((propKey) => {
    if (trigger.properties[propKey] === '') {
      delete trigger.properties[propKey];
    }
  });

  return trigger;
};

export const getDefaultTrigger = (type: ExtendedEventType): EventTrigger => {
  switch (type) {
    case EventType.UPDATER_APPLIED:
      return {
        type,
        properties: {
          updaterProfileName: '',
          updaterProfileCoordinate: '',
          updaterProfileType: '',
          compartmentSelectorInput: '',
        },
      } as UpdaterAppliedTrigger;
    case EventType.COMPARTMENT_FOUND:
      return {
        type,
        properties: {
          finderName: '',
          finderCoordinate: '',
          input: '',
        },
      };
    case EventType.DEVICE_TRIGGER:
      return {
        type,
        properties: {
          deviceTriggerType: 'button-pressed',
          deviceTriggerIndex: '',
          deviceId: '',
        },
      };
    case EventType.DEVICE_GEOFENCE:
      return {
        type,
        properties: {
          locationTrackingEventType: 'entry',
          fenceName: '',
        },
      };
    case EventType.EKANBAN_STATE_TRANSITION:
      return {
        type,
        properties: {
          receivingAreaName: '',
          replenishmentPlanName: '',
          fromState: '',
          toState: '',
        },
      };
    case EventType.PICKING_ORDER_STARTED:
    case EventType.PICKING_ORDER_CONCLUDED:
      return {
        type,
        properties: {
          externalId: '',
          zoneName: '',
        },
      };
    case 'dashboard':
      return {
        type: EventType.GENERIC,
        properties: {
          context: 'dashboard',
        },
      };
    case 'event-rule-action':
      return {
        type: EventType.GENERIC,
        properties: {
          context: 'event-rule-action',
          key: '',
        },
      };
    default:
      return {
        type: EventType.GENERIC,
        properties: {
          context: '',
          key: '',
        },
      };
  }
};

interface Context {
  eventTrigger?: EventTrigger;
  _triggers: readonly EventTrigger[];
  addTrigger: (trigger: EventTrigger) => void;
  removeTrigger: (index: number) => void;
  setTrigger: (index: number, trigger: EventTrigger) => void;
  setTriggers: (trigger?: EventTrigger) => void;
}

const [EventTriggerContext, useEventTrigger] = createContextHardened<Context>('Event Trigger');
export default useEventTrigger;

interface Props {
  children: React.ReactNode;
}

export const EventTriggerProvider = ({ children }: Props): React.JSX.Element => {
  const [triggers, setTriggers] = useState<EventTrigger[]>([]);

  const eventTrigger = useMemo((): EventTrigger | undefined => {
    if (triggers.length === 1) {
      return pruneUnsetProps(triggers[0]);
    }

    if (triggers.length > 1) {
      const prunedTriggers = triggers.map((t) => pruneUnsetProps(t));
      return {
        type: 'combined',
        properties: {
          triggers: prunedTriggers,
        },
      };
    }

    return undefined;
  }, [triggers]);

  const addTrigger = useCallback(
    (trigger: EventTrigger) => {
      triggers.push(trigger);
      setTriggers([...triggers]);
    },
    [triggers],
  );

  const removeTrigger = useCallback(
    (index: number) => {
      triggers.splice(index, 1);
      setTriggers([...triggers]);
    },
    [triggers],
  );

  const setTrigger = useCallback(
    (index: number, trigger: EventTrigger) => {
      triggers[index] = trigger;
      setTriggers([...triggers]);
    },
    [triggers],
  );

  return (
    <EventTriggerContext.Provider
      value={{
        eventTrigger,
        _triggers: triggers,
        addTrigger,
        removeTrigger,
        setTrigger,
        setTriggers: (trigger) => {
          if (trigger === undefined) {
            setTriggers([]);
          } else if (trigger?.type === 'combined') {
            const combinedTriggers = trigger?.properties.triggers as EventTrigger[];
            setTriggers(combinedTriggers ?? []);
          } else {
            setTriggers([trigger]);
          }
        },
      }}
    >
      {children}
    </EventTriggerContext.Provider>
  );
};
